import { AccessRole } from 'lib/const/AccessRole';
import { useAuth } from 'lib/context/auth';
import { useMemo } from 'react';
import { useAIFeatureFlags } from './useAIFeatureFlags';

export const useAIFeatureAvailability = () => {
  const { userData } = useAuth();
  const isAdminOrSupervisor = [
    AccessRole.ADMIN.toString(),
    AccessRole.SUPERVISOR.toString(),
  ].includes(userData.access.toString());
  const {
    isVinReelsEnabled,
    isQuickVideosEnabled,
    isAISpotlightEnabledForUser,
    isQuickVideoAvatarEnabledForUser,
    isAISpotlightEnabledForCustomer,
    isQuickVideoAvatarEnabledForCustomer,
    isSuggestedContentEnabled,
  } = useAIFeatureFlags();

  return useMemo(() => {
    // Suggested Content Configuration (AI Folders)
    const isSuggestedContentConfigurationAvailable = isSuggestedContentEnabled;

    // AI Assistants Configuration
    const isAIAssistantsConfigurationAvailable =
      isAdminOrSupervisor &&
      (isAISpotlightEnabledForCustomer ||
        (isQuickVideosEnabled && isQuickVideoAvatarEnabledForCustomer));

    // Pronunciation Configuration
    const isPronunciationConfigurationAvailable =
      isAdminOrSupervisor &&
      ((isQuickVideosEnabled && isQuickVideoAvatarEnabledForCustomer) ||
        isVinReelsEnabled ||
        isAISpotlightEnabledForCustomer);

    // Dealership Highlights Configuration
    const isDealershipHighlightsConfigurationAvailable =
      isAdminOrSupervisor && (isVinReelsEnabled || isQuickVideosEnabled);

    // Personalization Configuration
    const isPersonalizationConfigurationAvailable =
      isAdminOrSupervisor &&
      (isPronunciationConfigurationAvailable ||
        isDealershipHighlightsConfigurationAvailable);

    // Quick Videos Configuration
    const isQuickVideosConfigurationAvailable =
      isQuickVideosEnabled && isAdminOrSupervisor;

    // Vin Reels Feature
    const isVinReelsFeatureAvailable = isVinReelsEnabled;

    // Spotlight (flex video) Feature
    const isSpotlightFeatureAvailable = isAISpotlightEnabledForUser;

    // AI Assistant in Quick Videos Feature
    const isAIAssistantInQuickVideosFeatureAvailable =
      isQuickVideoAvatarEnabledForUser;

    // Configuration
    const isAIConfigurationAvailable =
      isAdminOrSupervisor &&
      (isAIAssistantsConfigurationAvailable ||
        isPersonalizationConfigurationAvailable ||
        isQuickVideosConfigurationAvailable);

    return {
      // configurations
      isPronunciationConfigurationAvailable,
      isDealershipHighlightsConfigurationAvailable,
      isPersonalizationConfigurationAvailable,
      isAIAssistantsConfigurationAvailable,
      isQuickVideosConfigurationAvailable,
      isAIConfigurationAvailable,
      // features
      isVinReelsFeatureAvailable,
      isSpotlightFeatureAvailable,
      isAIAssistantInQuickVideosFeatureAvailable,
      isSuggestedContentConfigurationAvailable,
    };
  }, [
    userData,
    isAdminOrSupervisor,
    isVinReelsEnabled,
    isQuickVideosEnabled,
    isAISpotlightEnabledForUser,
    isQuickVideoAvatarEnabledForUser,
    isAISpotlightEnabledForCustomer,
    isQuickVideoAvatarEnabledForCustomer,
    isSuggestedContentEnabled,
  ]);
};
